import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { Send, Link, DollarSign, Loader2, CheckCircle2, AlertCircle, MessageCircle, Mail, Phone, ChevronDown, ChevronUp, User } from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WavePaymentLinkModal = ({ open, onClose, operation, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);
  
  // Form state
  const [linkMode, setLinkMode] = useState('auto'); // 'auto' | 'manual'
  const [waveLink, setWaveLink] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [customPhone, setCustomPhone] = useState('');
  const [customEmail, setCustomEmail] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Result state
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open && operation?.id) {
      fetchFinancialSummary();
      fetchClientInfo();
      setResult(null);
      setLinkMode('auto');
      setWaveLink('');
      setNote('');
      setCustomPhone('');
      setCustomEmail('');
      setShowAdvanced(false);
    }
  }, [open, operation?.id]);

  const fetchClientInfo = async () => {
    try {
      const clientId = operation?.userId || operation?.clientId;
      if (!clientId) return;
      
      const response = await axios.get(
        `${API}/users/${clientId}`,
        {
          headers: { 'Authorization': 'Bearer cookie-auth' }
        }
      );
      setClientInfo(response.data);
      // Pré-remplir avec les données du client
      setCustomPhone(response.data?.phone || response.data?.telephone || '');
      setCustomEmail(response.data?.email || '');
    } catch (error) {
      console.error('Error fetching client info:', error);
      // Fallback sur les données de l'opération si disponibles
      setCustomPhone(operation?.clientPhone || '');
      setCustomEmail(operation?.clientEmail || '');
    }
  };

  const fetchFinancialSummary = async () => {
    setLoadingSummary(true);
    try {
      const response = await axios.get(
        `${API}/financial/${operation.id}/summary`,
        {
          headers: { 'Authorization': 'Bearer cookie-auth' }
        }
      );
      setFinancialSummary(response.data);
      
      // Pre-fill remaining amount
      if (response.data.amountRemaining > 0) {
        setAmount(response.data.amountRemaining.toString());
      }
    } catch (error) {
      console.error('Error fetching financial summary:', error);
      // Fallback: calculate from operation data
      const invoiceAmount = operation?.invoiceAmount || operation?.montantTotalTTC || 0;
      const amountPaid = operation?.amountPaid || 0;
      const remaining = invoiceAmount - amountPaid;
      setAmount(remaining > 0 ? remaining.toString() : '');
    } finally {
      setLoadingSummary(false);
    }
  };

  const validateForm = () => {
    if (linkMode === 'manual' && (!waveLink || !waveLink.trim())) {
      toast({
        title: "Champ manquant",
        description: "Veuillez coller le lien Wave",
        variant: "destructive"
      });
      return false;
    }
    
    if (!amount || parseFloat(amount) <= 0) {
      toast({
        title: "Champ manquant",
        description: "Le montant est obligatoire et doit être supérieur à 0",
        variant: "destructive"
      });
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    setResult(null);
    
    try {
      let finalLink = waveLink.trim();
      let generatedLinkId = null;

      // Mode auto : générer le lien via le module Checkout (sandbox / API Wave)
      if (linkMode === 'auto') {
        const genResp = await axios.post(`${API}/wave/checkout/links`, {
          invoiceId: operation.id,
          sourceType: 'operation',
          amount: parseInt(amount, 10),
          payerMobile: customPhone.trim() || null,
        });
        finalLink = genResp.data.waveLaunchUrl;
        if (finalLink?.startsWith('/')) finalLink = `${window.location.origin}${finalLink}`;
        generatedLinkId = genResp.data.id;
      }

      const response = await axios.post(
        `${API}/notifications/send-wave-payment-link`,
        {
          operation_id: operation.id,
          operation_number: operation.operationNumber,
          amount: parseFloat(amount),
          invoice_number: operation.invoiceNumber,
          wave_link: finalLink,
          note: note,
          // Champs optionnels pour override les coordonnées du client
          custom_phone: customPhone.trim() || null,
          custom_email: customEmail.trim() || null
        },
        {
          headers: {
            'Authorization': 'Bearer cookie-auth',
            'Content-Type': 'application/json'
          }
        }
      );

      setResult({ ...response.data, generatedLink: linkMode === 'auto' ? finalLink : null });

      // Tracer l'envoi sur le lien généré (best-effort)
      if (generatedLinkId) {
        const channels = [];
        if (response.data.whatsapp_sent) channels.push('whatsapp');
        if (response.data.email_sent) channels.push('email');
        if (channels.length) {
          axios.post(`${API}/wave/checkout/links/${generatedLinkId}/mark-sent`, { channels }).catch(() => {});
        }
      }
      
      if (response.data.success) {
        const channels = [];
        if (response.data.whatsapp_sent) channels.push('WhatsApp');
        if (response.data.email_sent) channels.push('Email');
        
        toast({
          title: "✅ Lien de paiement envoyé",
          description: `Envoyé via ${channels.join(' et ') || 'les canaux disponibles'}`,
        });
        
        if (onSuccess) {
          onSuccess(response.data);
        }
      } else {
        toast({
          title: "⚠️ Envoi partiel",
          description: response.data.errors?.join(', ') || "Certains canaux ont échoué",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error sending Wave payment link:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible d'envoyer le lien de paiement",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR').format(value);
  };

  const handleAmountChange = (e) => {
    // Only allow numbers
    const value = e.target.value.replace(/[^0-9]/g, '');
    setAmount(value);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-purple-700">
            <Send className="w-5 h-5" />
            Envoyer un lien de paiement Wave
          </DialogTitle>
          <DialogDescription>
            Le client recevra le lien de paiement par WhatsApp et Email
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* Operation Info */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Opération</span>
              <span className="font-semibold">{operation?.operationNumber}</span>
            </div>
            {operation?.invoiceNumber && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Facture</span>
                <span className="font-medium text-blue-600">{operation.invoiceNumber}</span>
              </div>
            )}
          </div>

          {/* Financial Summary */}
          {loadingSummary ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Chargement...</span>
            </div>
          ) : financialSummary && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xs text-gray-500">Total facture</p>
                  <p className="font-semibold text-gray-800">
                    {formatCurrency(financialSummary.totalAmount || 0)} F
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Déjà payé</p>
                  <p className="font-semibold text-green-600">
                    {formatCurrency(financialSummary.amountPaid || 0)} F
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Reste à payer</p>
                  <p className="font-semibold text-purple-700">
                    {formatCurrency(financialSummary.amountRemaining || 0)} F
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Mode de lien : auto (Checkout API) ou manuel */}
          <div className="grid grid-cols-2 gap-2" data-testid="link-mode-toggle">
            <button
              type="button"
              data-testid="link-mode-auto"
              onClick={() => setLinkMode('auto')}
              className={`p-2.5 rounded-lg border text-left text-sm transition-colors ${linkMode === 'auto' ? 'border-purple-400 bg-purple-50 text-purple-800 font-semibold' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            >
              ⚡ Génération automatique
              <div className="text-[11px] font-normal text-gray-500 mt-0.5">Lien sécurisé créé par la plateforme</div>
            </button>
            <button
              type="button"
              data-testid="link-mode-manual"
              onClick={() => setLinkMode('manual')}
              className={`p-2.5 rounded-lg border text-left text-sm transition-colors ${linkMode === 'manual' ? 'border-purple-400 bg-purple-50 text-purple-800 font-semibold' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            >
              📋 Coller un lien
              <div className="text-[11px] font-normal text-gray-500 mt-0.5">Lien généré depuis l'app Wave</div>
            </button>
          </div>

          {/* Wave Link Input (mode manuel) */}
          {linkMode === 'manual' ? (
            <div className="space-y-2">
              <Label htmlFor="waveLink" className="flex items-center gap-2">
                <Link className="w-4 h-4 text-purple-600" />
                Lien Wave <span className="text-red-500">*</span>
              </Label>
              <Input
                id="waveLink"
                value={waveLink}
                onChange={(e) => setWaveLink(e.target.value)}
                placeholder="Collez le lien Wave généré ici..."
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500">
                Générez le lien depuis l&apos;application Wave et collez-le ici
              </p>
            </div>
          ) : (
            <Alert className="bg-purple-50 border-purple-200">
              <AlertDescription className="text-xs text-purple-700">
                Un lien de paiement rattaché à la facture <strong>{operation?.invoiceNumber}</strong> sera généré
                automatiquement{customPhone ? <> et réservé au n° <strong>{customPhone}</strong></> : null}.
                Le paiement sera lettré automatiquement à réception.
              </AlertDescription>
            </Alert>
          )}

          {/* Amount Input */}
          <div className="space-y-2">
            <Label htmlFor="amount" className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-600" />
              Montant demandé (FCFA) <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Input
                id="amount"
                value={amount}
                onChange={handleAmountChange}
                placeholder="Ex: 100000"
                className="pr-16 text-lg font-semibold"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                FCFA
              </span>
            </div>
            {amount && (
              <p className="text-sm text-purple-600 font-medium">
                💰 {formatCurrency(parseFloat(amount) || 0)} FCFA
              </p>
            )}
          </div>

          {/* Note (optional) */}
          <div className="space-y-2">
            <Label htmlFor="note">Note (optionnel)</Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ajoutez une note pour le client..."
              rows={2}
              className="resize-none"
            />
          </div>

          {/* Advanced Options - Destination personnalisée */}
          <div className="border rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            >
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Destinataire personnalisé</span>
                {(customPhone || customEmail) && (
                  <span className="text-xs text-gray-500">(pré-rempli)</span>
                )}
              </div>
              {showAdvanced ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </button>
            
            {showAdvanced && (
              <div className="p-3 space-y-3 bg-white border-t">
                <p className="text-xs text-gray-500 mb-2">
                  Laissez vide pour utiliser les coordonnées du profil client
                </p>
                
                {/* Custom Phone */}
                <div className="space-y-1">
                  <Label htmlFor="customPhone" className="text-sm flex items-center gap-2">
                    <Phone className="w-3 h-3 text-green-600" />
                    N° WhatsApp
                  </Label>
                  <Input
                    id="customPhone"
                    value={customPhone}
                    onChange={(e) => setCustomPhone(e.target.value)}
                    placeholder="+225 07 XX XX XX XX"
                    className="text-sm"
                  />
                </div>
                
                {/* Custom Email */}
                <div className="space-y-1">
                  <Label htmlFor="customEmail" className="text-sm flex items-center gap-2">
                    <Mail className="w-3 h-3 text-blue-600" />
                    Adresse Email
                  </Label>
                  <Input
                    id="customEmail"
                    type="email"
                    value={customEmail}
                    onChange={(e) => setCustomEmail(e.target.value)}
                    placeholder="client@example.com"
                    className="text-sm"
                  />
                </div>

                {clientInfo && (
                  <div className="text-xs text-gray-400 pt-2 border-t">
                    <span className="font-medium">Profil client :</span>{' '}
                    {clientInfo.firstName} {clientInfo.lastName}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Channels Info */}
          <Alert className="bg-blue-50 border-blue-200">
            <AlertDescription className="text-sm text-blue-700">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <MessageCircle className="w-4 h-4 text-green-600" />
                  <span>WhatsApp</span>
                </div>
                <div className="flex items-center gap-1">
                  <Mail className="w-4 h-4 text-blue-600" />
                  <span>Email</span>
                </div>
              </div>
              <p className="mt-1 text-xs">Le lien sera envoyé sur les deux canaux simultanément</p>
            </AlertDescription>
          </Alert>

          {/* Result */}
          {result && (
            <Alert className={result.success ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}>
              <AlertDescription>
                <div className="flex items-start gap-2">
                  {result.success ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className={`font-medium ${result.success ? 'text-green-800' : 'text-amber-800'}`}>
                      {result.success ? 'Envoi réussi' : 'Envoi partiel'}
                    </p>
                    <div className="flex gap-4 mt-1 text-sm">
                      <span className={result.whatsapp_sent ? 'text-green-600' : 'text-gray-400'}>
                        {result.whatsapp_sent ? '✓' : '✗'} WhatsApp
                      </span>
                      <span className={result.email_sent ? 'text-green-600' : 'text-gray-400'}>
                        {result.email_sent ? '✓' : '✗'} Email
                      </span>
                    </div>
                    {result.errors?.length > 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        {result.errors.join(', ')}
                      </p>
                    )}
                    {result.generatedLink && (
                      <p className="text-[11px] text-gray-500 mt-1 break-all">
                        Lien : {result.generatedLink}
                      </p>
                    )}
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2 border-t">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {result?.success ? 'Fermer' : 'Annuler'}
          </Button>
          {!result?.success && (
            <Button
              onClick={handleSubmit}
              disabled={loading || (linkMode === 'manual' && !waveLink) || !amount}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="send-wave-link-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer le lien
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WavePaymentLinkModal;
